import { Component, OnDestroy, OnInit, effect } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PaymentService } from '../payment/payment.service';
import { paymentInfo } from '../../types/paymentInfo';
import { gigyaInfo } from '../../types/gigyaInfo';
import { LoginService } from '../login/login.service';
import { TranslateService } from '@ngx-translate/core';
import { InvoiceInfo } from '../../types/InvoiceInfo';
import { JwtService } from '../../shared/services/jwt/jwt.service';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { PopupService } from '../../core/services/popup/popup.service';
import { AuthService } from '../../core/services/auth/auth.service';

@Component({
    selector: 'app-payment-response',
    templateUrl: './paymentResponse.component.html',
    styleUrls: ['./paymentResponse.component.scss'],
    standalone: false
})

export class PaymentResponseComponent implements OnInit, OnDestroy {
    
    paymentStatus = '';
    errorMsg = '';
    errorPop = false;
    paymentDetails: paymentInfo = new paymentInfo();
    amountDetails: any = {};
    resp: any;
    userId = 0;
    previousRoute = '';
    invoiceInfo: InvoiceInfo = new InvoiceInfo();

    private processed = false; // guard to ensure getPayResponse called once

    constructor(public activateRoute: ActivatedRoute, private paymentService: PaymentService,
                private translate: TranslateService, private loginService: LoginService, private router: Router,
                private jwtService: JwtService, private popupService: PopupService, private authService: AuthService) {}

    ngOnInit() {

        // Read snapshots from signals (already hydrated from sessionStorage in services)
        this.resp = this.loginService.getGigyaResp();
        this.paymentDetails = this.paymentService.paymentDetails();
        this.previousRoute = this.paymentService.redirectComp();

        // Determine best amount details precedence
        const asst = this.paymentService.assistanceAmountChosen();
        const adt = this.paymentService.adtAmountChosen();
        const clip = this.paymentService.clipAmountChosen();
        const clipAdt = this.paymentService.clipAdtAmountChosen();
        const doc = this.paymentService.amountChosen();

        if(this.previousRoute && this.previousRoute === 'assistance' && asst && Object.keys(asst).length) {
            this.amountDetails = asst;
        } else if(doc && Object.keys(doc).length) {
            this.amountDetails = doc;
        } else if(adt && Object.keys(adt).length) {
            this.amountDetails = adt;
        } else if(clip && Object.keys(clip).length) {
            this.amountDetails = clip;
        } else if(clipAdt && Object.keys(clipAdt).length) {
            this.amountDetails = clipAdt;
        }

        if(this.resp) {
            this.userId = this.resp.userId;
        }

        // Get paymentStatus from query params (single subscription)
        this.activateRoute.queryParams.subscribe((params: Params) => {
            this.paymentStatus = params['paymentStatus'];
            this.tryProcess();
        });

        // In case user data or payment details change reactively later (rare after redirect)
        effect(() => {
            const gigya = this.loginService.getGigyaResp();
            const pay = this.paymentService.paymentDetails();
            const route = this.paymentService.redirectComp();
            if(!this.processed && gigya && pay && route) {
                this.resp = gigya;
                this.paymentDetails = pay;
                this.previousRoute = route;
                this.tryProcess();
            }
        });
    }
    private tryProcess() {
        if(this.processed) return;
        if(this.resp && this.paymentDetails && this.amountDetails && this.paymentStatus) {
            this.processed = true;
            this.getPayResponse();
        }
    }
    getPayResponse() {
        this.errorMsg = '';
        if(this.paymentStatus === 'AUTHORISED') {
            if(this.amountDetails.userId) {
                this.invoiceInfo.userId = this.amountDetails.userId;
            }
            if(this.amountDetails.userIds) {
                this.invoiceInfo.userIds = this.amountDetails.userIds;
            }
            this.invoiceInfo.invoiceOrderCode = this.paymentDetails.orderCode;
            this.invoiceInfo.invoicePaymentStatus = this.paymentStatus;
            if(this.amountDetails.reprogId) {
                this.invoiceInfo.reprogId = this.amountDetails.reprogId;
            } else if(this.amountDetails.adtSubscriptionOfferId) {
                this.invoiceInfo.adtSubscriptionOfferId = this.amountDetails.adtSubscriptionOfferId;
                this.invoiceInfo.countryCode = this.amountDetails.countryCode;                
                this.invoiceInfo.parentId = this.amountDetails.parentId;
                this.invoiceInfo.languageCode = this.amountDetails.languageCode;
            } else if(this.amountDetails.clipSubscriptionOfferId) {
                this.invoiceInfo.clipSubscriptionOfferId = this.amountDetails.clipSubscriptionOfferId;
                this.invoiceInfo.countryCode = this.amountDetails.countryCode;                
                this.invoiceInfo.parentId = this.amountDetails.parentId;
                this.invoiceInfo.languageCode = this.amountDetails.languageCode;
            } else if(this.amountDetails.clipAdtSubscriptionOfferId) {
                this.invoiceInfo.clipAdtSubscriptionOfferId = this.amountDetails.clipAdtSubscriptionOfferId;
                this.invoiceInfo.countryCode = this.amountDetails.countryCode;                
                this.invoiceInfo.parentId = this.amountDetails.parentId;
                this.invoiceInfo.languageCode = this.amountDetails.languageCode;
            } else {
                this.invoiceInfo.assistanceOfferId = this.amountDetails.assistanceOfferId;
            }
            if(this.amountDetails.type !== 'ADT_SUBSCRIPTION') {
                this.invoiceInfo.priceId = this.amountDetails.priceId;
            }
            this.invoiceInfo.type = this.amountDetails.type;
            if(this.amountDetails.modLanguage) {
                this.invoiceInfo.modLanguage = this.amountDetails.modLanguage;
            }
            this.invoiceInfo.invoicePaymentReference = this.paymentDetails.referenceId;
            this.invoiceInfo.invoicePriceHtc = this.amountDetails.priceHTC;
            this.invoiceInfo.invoicePriceTtc = this.amountDetails.priceTTC;
            this.invoiceInfo.invoicePriceVat = this.amountDetails.priceVAT;
            this.invoiceInfo.vatPercent = this.amountDetails.vatPercent;
            if(this.amountDetails.assitanceTokenPurchasedQuantity) {
                this.invoiceInfo.assitanceTokenPurchasedQuantity = this.amountDetails.assitanceTokenPurchasedQuantity;
            }
            if(this.amountDetails.type === 'ADT_SUBSCRIPTION') {
                let invoiceData: string = this.jwtService.encode(this.invoiceInfo, "secret");
                this.paymentService.insertAdtInvoice(invoiceData).subscribe((response: any) => { 
                    if(response && response.errorCode === '0') {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_SUCCESS") }
                        });
                        this.loginService.setGigyaResponse(this.resp);
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    } else {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_INVOICE_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                },  (error: any) => {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                );
            } else if(this.amountDetails.type === 'CLIP_SUBSCRIPTION') {
                let invoiceData: string = this.jwtService.encode(this.invoiceInfo, "secret");
                this.paymentService.insertClipInvoice(invoiceData).subscribe((response: any) => { 
                    if(response && response.errorCode === '0') {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_SUCCESS") }
                        });
                        this.loginService.setGigyaResponse(this.resp);
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    } else {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_INVOICE_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                },  (error: any) => {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                );
            } else if(this.amountDetails.type === 'CLIP_ADT_SUBSCRIPTION') {
                let invoiceData: string = this.jwtService.encode(this.invoiceInfo, "secret");
                this.paymentService.insertClipAdtInvoice(invoiceData).subscribe((response: any) => { 
                    if(response && response.errorCode === '0') {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_SUCCESS") }
                        });
                        this.loginService.setGigyaResponse(this.resp);
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    } else {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_INVOICE_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                },  (error: any) => {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
                        });
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                );
            } else {
                let invoiceData: string = this.jwtService.encode(this.invoiceInfo, "secret");
                this.paymentService.insertInvoice(invoiceData).subscribe((response: any) => { 
                    if(response && response.errorCode === '0') {
                        this.popupService.openDialog(MessagePopupComponent, {
                            data: { message: this.translate.instant("MSG_PAYMENT_SUCCESS") }
                        });
                        if(this.invoiceInfo.type === 'DO') {
                            this.resp.subscribeStatus = true;
                        } else if(this.invoiceInfo.type === 'ADT') {
                            this.resp.adtModuleBought = true;
                        } else if(this.invoiceInfo.type === 'CLIP') {
                            this.resp.clipModuleBought = true;
                        } else if(this.invoiceInfo.type === 'AO') {
                            this.resp.ticketBal += this.amountDetails.assitanceTokenPurchasedQuantity;
                        } else if(this.invoiceInfo.type === 'PT' && this.userId == this.amountDetails.userId) {
                            this.resp.ptokenBought = true;
                        } else if(this.invoiceInfo.type === 'VCI' && this.userId == this.amountDetails.userId) {
                            this.resp.vciModuleBought = true;
                        }
                        this.loginService.setGigyaResponse(this.resp);
                        this.authService.setLoggedInUser(this.resp);
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    } else {
                        this.errorMsg = this.translate.instant("MSG_PAYMENT_INVOICE_ERROR");
                        this.errorPop = true;
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                }, (error: any) => {
                        this.errorMsg = this.translate.instant("MSG_TRANSACTION_ERROR");
                        this.errorPop = true;
                        setTimeout(() => {
                            this.backToHome();
                        }, 500);
                    }
                );
            }  
        } else if(this.paymentStatus === 'REFUSED') {
            this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("MSG_TRANSACTION_DECLINED") }
            });
            setTimeout(() => {
                this.backToHome();
            }, 500);
        } else if(this.paymentStatus === 'SOFT_DECLINED') {
            this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("MSG_TRANSACTION_DECLINED") }
            });
            setTimeout(() => {
                this.backToHome();
            }, 500);
        } else if(this.paymentStatus === 'ERROR') {
            this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
            });
            setTimeout(() => {
                this.backToHome();
            }, 500);
        } else if(this.paymentStatus === 'CANCELLED') {
            this.popupService.openDialog(MessagePopupComponent, {
                data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
            });
            setTimeout(() => {
                this.backToHome();
            }, 500);
        } else {
            this.invoiceInfo.userId = this.amountDetails.userId;
            this.invoiceInfo.invoiceOrderCode = this.paymentDetails.orderCode;
            this.invoiceInfo.invoicePaymentStatus = 'WAITING_FOR_WORLDPAY_RESPONSE';
            if(this.amountDetails.reprogId) {
                this.invoiceInfo.reprogId = this.amountDetails.reprogId;
            } else {
                this.invoiceInfo.assistanceOfferId = this.amountDetails.assistanceOfferId;
            }
            this.invoiceInfo.priceId = this.amountDetails.priceId;
            this.invoiceInfo.type = this.amountDetails.type;
            this.invoiceInfo.modLanguage = this.amountDetails.modLanguage;
            this.invoiceInfo.invoicePaymentReference = this.paymentDetails.referenceId;
            this.invoiceInfo.invoicePriceHtc = this.amountDetails.priceHTC;
            this.invoiceInfo.invoicePriceTtc = this.amountDetails.priceTTC;
            this.invoiceInfo.invoicePriceVat = this.amountDetails.priceVAT;
            this.invoiceInfo.vatPercent = this.amountDetails.vatPercent;
            if(this.amountDetails.assitanceTokenPurchasedQuantity) {
                this.invoiceInfo.assitanceTokenPurchasedQuantity = this.amountDetails.assitanceTokenPurchasedQuantity;
            }
            let invoiceData: string = this.jwtService.encode(this.invoiceInfo, "secret");
            this.paymentService.insertInvoice(invoiceData).subscribe((response: any) => { 
                if(response && response.errorCode === '0') {
                    this.popupService.openDialog(MessagePopupComponent, {
                        data: { message: this.translate.instant("MSG_PAYMENT_PENDING") }
                    });
                    if(this.invoiceInfo.type === 'DO') {
                        this.resp.subscribeStatus = false;
                    } else if(this.invoiceInfo.type === 'ADT') {
                        this.resp.adtModuleBought = false;
                    } else if(this.invoiceInfo.type === 'CLIP') {
                        this.resp.clipModuleBought = true;
                    } else if(this.invoiceInfo.type === 'AO') {
                        this.resp.ticketBal += this.amountDetails.assitanceTokenPurchasedQuantity;
                    } else if(this.invoiceInfo.type === 'PT' && this.userId == this.amountDetails.userId) {
                        this.resp.ptokenBought = false;
                    } else if(this.invoiceInfo.type === 'VCI' && this.userId == this.amountDetails.userId) {
                        this.resp.vciModuleBought = true;
                    }
                    this.loginService.setGigyaResponse(this.resp);
                    setTimeout(() => {
                        this.backToHome();
                    }, 500);
                } else {
                    this.popupService.openDialog(MessagePopupComponent, {
                        data: { message: this.translate.instant("MSG_PAYMENT_INVOICE_ERROR") }
                    });
                    setTimeout(() => {
                        this.backToHome();
                    }, 500);
                }
            }, (error: any) => {
                    this.popupService.openDialog(MessagePopupComponent, {
                        data: { message: this.translate.instant("MSG_TRANSACTION_ERROR") }
                    });
                    setTimeout(() => {
                        this.backToHome();
                    }, 500);
                }
            );
        }
    }
    backToHome() {
        this.clearPaymentToken(); // Clear token before navigating away
        this.router.navigateByUrl(this.previousRoute);
    }
    private clearPaymentToken() {
        sessionStorage.removeItem('currentToken');
    }
    ngOnDestroy() {
        this.clearPaymentToken(); // Clear token before navigating away
        this.paymentService.clearAmountDetails();
    }
}